// Carrinho do redesign: mesma base do CartDrawer Vnda, com sugestões e
// empty state ("Conheça nossos produtos"). O legacy em common/cartDrawer
// continua intacto.

import CartDrawer from '../../common/cartDrawer';

const SUGGESTED_TAG = 'home-destaques';
const TITLE_WITH_ITEMS = 'Que tal complementar seu pedido?';
const TITLE_EMPTY = 'Conheça nossos produtos';
const EMPTY_ROOT_CLASS = 'rd-cart-empty-suggest';

function formatPrice(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return '';
  return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function productImage(product) {
  return (
    product.image_url ||
    product.image ||
    (product.images && product.images[0] && (product.images[0].url || product.images[0])) ||
    ''
  );
}

function productSku(product) {
  if (product.sku) return product.sku;
  if (product.variants && product.variants.length === 1) {
    return product.variants[0].sku;
  }
  return '';
}

async function fetchCart() {
  try {
    const response = await fetch('/carrinho', { headers: { Accept: 'application/json' } });
    if (!response.ok) return { items: [] };
    return await response.json();
  } catch (error) {
    console.error('Erro ao buscar carrinho', error);
    return { items: [] };
  }
}

// Evita martelar /carrinho a cada MutationObserver (pode gerar "Retry later").
let cartCache = { at: 0, data: null };
const CART_CACHE_MS = 3000;

async function fetchCartCached(force = false) {
  const now = Date.now();
  if (!force && cartCache.data && now - cartCache.at < CART_CACHE_MS) {
    return cartCache.data;
  }
  const data = await fetchCart();
  cartCache = { at: now, data };
  return data;
}

async function fetchSuggested() {
  try {
    const response = await fetch(`/carrinho/produtos-sugeridos/${SUGGESTED_TAG}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : data.products || data.items || [];
  } catch (error) {
    console.error('Erro ao buscar produtos sugeridos', error);
    return [];
  }
}

function cartProductIds(cart) {
  const ids = new Set();
  (cart.items || []).forEach((item) => {
    if (item.product_id != null) ids.add(String(item.product_id));
    if (item.product_name) ids.add(item.product_name.trim().toLowerCase());
    if (item.name) ids.add(item.name.trim().toLowerCase());
  });
  return ids;
}

function isAlreadyInCart(product, ids) {
  if (product.id != null && ids.has(String(product.id))) return true;
  if (product.product_id != null && ids.has(String(product.product_id))) return true;
  const name = (product.name || product.product_name || '').trim().toLowerCase();
  return name !== '' && ids.has(name);
}

async function addSuggestedProduct(product) {
  const sku = productSku(product);
  if (!sku) {
    // Sem SKU único: abre a PDP para escolher variante.
    if (product.url || product.product_url) {
      window.location.href = product.url || product.product_url;
    }
    return;
  }

  const body = new URLSearchParams();
  body.set('sku', sku);
  body.set('quantity', '1');

  await fetch('/carrinho/adicionar', {
    method: 'POST',
    headers: {
      Accept: 'application/json, text/javascript, */*; q=0.0',
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body: body.toString(),
  });

  CartDrawer.updateCartCount();
  // Reabre/atualiza o drawer (a instância Vnda revalida no open).
  if (CartDrawer.settings && CartDrawer.settings.instance) {
    CartDrawer.settings.instance.open();
  }
}

function buildEmptySuggest(products) {
  const root = document.createElement('div');
  root.className = EMPTY_ROOT_CLASS;

  const title = document.createElement('h4');
  title.className = 'rd-cart-empty-suggest__title';
  title.textContent = TITLE_EMPTY;
  root.appendChild(title);

  const list = document.createElement('ul');
  list.className = 'rd-cart-empty-suggest__list';

  products.forEach((product) => {
    const li = document.createElement('li');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'rd-cart-empty-suggest__item';
    button.setAttribute('data-product-id', product.id || product.product_id || '');

    const thumb = document.createElement('span');
    thumb.className = 'rd-cart-empty-suggest__thumb';
    const imgUrl = productImage(product);
    if (imgUrl) {
      const img = document.createElement('img');
      img.src = imgUrl;
      img.alt = product.name || '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    }

    const info = document.createElement('span');
    info.className = 'rd-cart-empty-suggest__info';

    const name = document.createElement('span');
    name.className = 'rd-cart-empty-suggest__name';
    name.textContent = product.name || product.product_name || '';

    const price = document.createElement('span');
    price.className = 'rd-cart-empty-suggest__price';
    price.textContent = formatPrice(
      product.on_sale ? product.sale_price : product.price || product.sale_price
    );

    info.appendChild(name);
    info.appendChild(price);

    const add = document.createElement('span');
    add.className = 'rd-cart-empty-suggest__add';
    add.setAttribute('aria-hidden', 'true');

    button.appendChild(thumb);
    button.appendChild(info);
    button.appendChild(add);
    button.addEventListener('click', () => addSuggestedProduct(product));

    li.appendChild(button);
    list.appendChild(li);
  });

  root.appendChild(list);
  return root;
}

async function syncEmptySuggest(drawerRoot) {
  const emptyMsg = [...drawerRoot.querySelectorAll('p')].find((p) =>
    /seu carrinho est/i.test(p.textContent || '')
  );
  const existing = drawerRoot.querySelector(`.${EMPTY_ROOT_CLASS}`);

  if (!emptyMsg) {
    if (existing) existing.remove();
    return;
  }

  const cart = await fetchCartCached();
  if ((cart.items || []).length > 0) {
    if (existing) existing.remove();
    return;
  }

  const products = await fetchSuggested();
  const ids = cartProductIds(cart);
  const filtered = products.filter((product) => !isAlreadyInCart(product, ids)).slice(0, 4);

  if (filtered.length === 0) {
    if (existing) existing.remove();
    return;
  }

  const next = buildEmptySuggest(filtered);
  const signature = filtered.map((p) => p.id || p.product_id || p.name).join('|');
  next.dataset.signature = signature;

  if (existing) {
    if (existing.dataset.signature === signature) return;
    existing.replaceWith(next);
  } else {
    const host = emptyMsg.closest('.cart-drawer-flex') || emptyMsg.parentElement;
    if (host) host.appendChild(next);
  }
}

function filterNativeSuggested(drawerRoot, cart) {
  const ids = cartProductIds(cart);
  drawerRoot.querySelectorAll('.vnda-suggested-item').forEach((el) => {
    const nameEl = el.querySelector('h5');
    const name = (nameEl && nameEl.textContent ? nameEl.textContent : '').trim().toLowerCase();
    const hide = name !== '' && ids.has(name);
    el.style.display = hide ? 'none' : '';

    // Esconde o slide do carrossel, senão sobra buraco na lista
    const slide = el.closest('.cart-drawer-item');
    if (slide) slide.style.display = hide ? 'none' : '';
  });
}

function couponDiscountLabel(cart) {
  const subtotal = Number(cart.subtotal);
  const total = Number(cart.total);
  if (!Number.isFinite(subtotal) || !Number.isFinite(total) || subtotal <= total) {
    return '';
  }

  const saved = subtotal - total;
  const percent = Math.round((saved / subtotal) * 100);
  if (percent > 0 && percent <= 100) {
    return `−${percent}% · ${formatPrice(saved)}`;
  }
  return `−${formatPrice(saved)}`;
}

function polishCouponForm(drawerRoot) {
  drawerRoot.querySelectorAll('.cart-drawer-items-baseline form label').forEach((label) => {
    const text = (label.textContent || '').trim();
    if (/adicionar cupom/i.test(text) || /insira seu cupom/i.test(text)) {
      label.textContent = 'Insira seu cupom de desconto';
    }
  });

  drawerRoot.querySelectorAll('.cart-drawer-items-baseline form button').forEach((button) => {
    button.setAttribute('aria-label', 'Aplicar cupom');
  });

  // Botão nativo "cancelar" → só ícone X (o CSS esconde o texto)
  drawerRoot.querySelectorAll('.cart-drawer-items-baseline button.cart-drawer-self-end').forEach((button) => {
    if (/cancelar/i.test(button.textContent || '')) {
      button.textContent = '';
      button.setAttribute('aria-label', 'Fechar cupom');
    }
  });
}

function polishCouponApplied(drawerRoot, cart) {
  const blocks = drawerRoot.querySelectorAll('.cart-drawer-items-baseline');

  blocks.forEach((block) => {
    const appliedP = [...block.querySelectorAll('p')].find((p) =>
      /cupom adicionado/i.test(p.textContent || '')
    );
    const editBtn = [...block.querySelectorAll('button')].find((b) =>
      /^editar$/i.test((b.textContent || '').trim())
    );

    if (!appliedP || !editBtn) {
      const stale = block.querySelector('.rd-cart-coupon-applied');
      if (stale) stale.remove();
      return;
    }

    // Esconde o markup nativo; mostra o bloco restyled.
    appliedP.hidden = true;
    editBtn.hidden = true;

    const codeMatch = (appliedP.textContent || '').replace(/cupom adicionado:\s*/i, '').trim();
    const discount = couponDiscountLabel(cart);
    const signature = `${codeMatch}|${discount}`;

    let card = block.querySelector('.rd-cart-coupon-applied');
    if (card && card.dataset.signature === signature) {
      appliedP.hidden = true;
      editBtn.hidden = true;
      return;
    }

    if (!card) {
      card = document.createElement('div');
      card.className = 'rd-cart-coupon-applied';
      block.insertBefore(card, appliedP);
    }

    card.dataset.signature = signature;
    card.innerHTML = '';

    const text = document.createElement('div');
    text.className = 'rd-cart-coupon-applied__text';

    const code = document.createElement('span');
    code.className = 'rd-cart-coupon-applied__code';
    code.textContent = codeMatch || 'Cupom aplicado';
    text.appendChild(code);

    if (discount) {
      const disc = document.createElement('span');
      disc.className = 'rd-cart-coupon-applied__discount';
      disc.textContent = discount;
      text.appendChild(disc);
    }

    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'rd-cart-coupon-applied__edit';
    edit.textContent = 'Editar';
    edit.addEventListener('click', () => editBtn.click());

    card.appendChild(text);
    card.appendChild(edit);
  });
}

function normalizeCartKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function cartItemIsSoldOut(item) {
  if (!item) return false;

  if (item.available === false || item.product_available === false) return true;
  if (item.available === 0 || item.available === '0') return true;

  const stock =
    item.available_quantity ??
    item.quantity_available ??
    item.stock ??
    (item.extra && (item.extra.available_quantity ?? item.extra.stock));

  if (stock != null && Number(stock) <= 0) return true;

  // Carrinho pediu mais do que o estoque atual.
  if (stock != null && item.quantity != null && Number(item.quantity) > Number(stock)) {
    return true;
  }

  return false;
}

// O /carrinho às vezes mantém available:true em itens já adicionados
// mesmo com estoque 0. Cruzamos com o JSON do produto.
const productCache = new Map();
const PRODUCT_CACHE_MS = 30000;

async function fetchProductCached(url) {
  if (!url) return null;

  const key = String(url).split('?')[0];
  const cached = productCache.get(key);
  if (cached && Date.now() - cached.at < PRODUCT_CACHE_MS) {
    return cached.data;
  }

  try {
    const response = await fetch(key, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
      productCache.set(key, { at: Date.now(), data: null });
      return null;
    }
    const data = await response.json();
    productCache.set(key, { at: Date.now(), data });
    return data;
  } catch (error) {
    console.error('Erro ao buscar produto do item do carrinho', error);
    productCache.set(key, { at: Date.now(), data: null });
    return null;
  }
}

function variantLooksSoldOut(variant) {
  if (!variant) return false;
  if (variant.available === false) return true;
  const stock = variant.available_quantity ?? variant.stock ?? variant.quantity;
  return stock != null && Number(stock) <= 0;
}

async function cartItemIsSoldOutResolved(item) {
  if (cartItemIsSoldOut(item)) {
    return { sold: true, labels: [item.variant_name] };
  }

  const productUrl = item.product_url || item.url;
  const sku = item.variant_sku || item.sku;
  if (!productUrl || !sku) return { sold: false, labels: [] };

  const product = await fetchProductCached(productUrl);
  const variants = product && Array.isArray(product.variants) ? product.variants : [];
  const variant = variants.find(
    (entry) =>
      normalizeCartKey(entry.sku) === normalizeCartKey(sku) ||
      String(entry.id) === String(item.variant_id)
  );

  if (!variantLooksSoldOut(variant)) return { sold: false, labels: [] };

  return {
    sold: true,
    labels: [item.variant_name, variant && variant.name],
  };
}

async function resolveCartSoldOutItems(cart) {
  const items = cart.items || [];
  return Promise.all(
    items.map(async (item) => {
      const result = await cartItemIsSoldOutResolved(item);
      return { item, sold: result.sold, labels: result.labels || [] };
    })
  );
}

function readCartDomEventJson(el) {
  const node = el.querySelector('[data-event-json]');
  if (!node) return null;

  try {
    return JSON.parse(node.getAttribute('data-event-json') || '');
  } catch (error) {
    return null;
  }
}

function domMatchesSoldCartItem(el, item, labels = []) {
  const eventJson = readCartDomEventJson(el);
  const sku = item.variant_sku || item.sku;

  if (eventJson) {
    if (item.id != null && String(eventJson.item_id) === String(item.id)) return true;
    if (sku && normalizeCartKey(eventJson.item_variant) === normalizeCartKey(sku)) return true;
  }

  const link = el.querySelector('.cart-list-item-name a');
  const variantEl = el.querySelector('.cart-list-item-variant-name');
  const productName = normalizeCartKey(item.product_name || item.name);
  const domName = normalizeCartKey(link && link.textContent);
  const domVariant = normalizeCartKey(variantEl && variantEl.textContent);
  const variantLabels = [item.variant_name, ...labels]
    .filter(Boolean)
    .map((label) => normalizeCartKey(label));

  if (productName && domName && productName === domName && domVariant && variantLabels.includes(domVariant)) {
    return true;
  }

  if (link && link.href && (item.product_url || item.url) && domVariant && variantLabels.includes(domVariant)) {
    try {
      const domPath = new URL(link.href, window.location.origin).pathname;
      const itemPath = new URL(item.product_url || item.url, window.location.origin).pathname;
      if (domPath === itemPath) return true;
    } catch (error) {
      // ignore
    }
  }

  return false;
}

function ensureSoldOutLabel(itemEl) {
  let label = itemEl.querySelector('.rd-cart-item__sold-out');
  if (label) return label;

  label = document.createElement('p');
  label.className = 'rd-cart-item__sold-out';
  label.textContent = 'Esgotado';

  // Preferência: no bloco de preço/qty (como no Figma).
  const priceRow = itemEl.querySelector('.cart-list-item-name + .cart-drawer-flex');
  const price = itemEl.querySelector('.cart-list-item-price');
  if (priceRow) {
    priceRow.insertBefore(label, priceRow.firstChild);
  } else if (price && price.parentElement) {
    price.parentElement.insertBefore(label, price);
  } else {
    const details = itemEl.querySelector('.cart-drawer-tracking-tight > div:last-child');
    if (details) details.appendChild(label);
  }

  return label;
}

async function polishSoldOutItems(drawerRoot, resolved) {
  const items = [...drawerRoot.querySelectorAll('.cart-item.cart-list-item')];
  const soldEls = new Set();

  resolved.forEach(({ item, sold, labels }) => {
    if (!sold) return;

    const match = items.find((el) => !soldEls.has(el) && domMatchesSoldCartItem(el, item, labels));
    if (match) soldEls.add(match);
  });

  items.forEach((itemEl) => {
    const sold = soldEls.has(itemEl);

    itemEl.classList.toggle('rd-cart-item--sold-out', sold);
    itemEl.toggleAttribute('data-rd-cart-sold-out', sold);

    if (sold) {
      ensureSoldOutLabel(itemEl);
    } else {
      const label = itemEl.querySelector('.rd-cart-item__sold-out');
      if (label) label.remove();
    }
  });
}

// Estado do checkout com itens esgotados (usado no clique do CTA).
let checkoutSoldState = {
  soldItems: [],
  availableItems: [],
  availableTotal: 0,
};

function itemLineTotal(item) {
  const total = Number(item.total ?? item.subtotal ?? Number(item.price) * Number(item.quantity || 1));
  return Number.isFinite(total) ? total : 0;
}

function polishCheckoutCta(drawerRoot, resolved) {
  const cta = drawerRoot.querySelector('.card-drawer-cta');
  if (!cta) return;

  const soldItems = resolved.filter((entry) => entry.sold).map((entry) => entry.item);
  const availableItems = resolved.filter((entry) => !entry.sold).map((entry) => entry.item);
  const availableTotal = availableItems.reduce((sum, item) => sum + itemLineTotal(item), 0);

  checkoutSoldState = { soldItems, availableItems, availableTotal };

  const label = cta.querySelector('span:first-of-type');
  const price = cta.querySelector('span + span');
  const allSold = resolved.length > 0 && availableItems.length === 0;
  const mixed = soldItems.length > 0 && availableItems.length > 0;

  cta.classList.toggle('rd-cart-cta--blocked', allSold);
  cta.classList.toggle('rd-cart-cta--mixed', mixed);
  cta.toggleAttribute('disabled', allSold);
  cta.setAttribute('aria-disabled', allSold ? 'true' : 'false');

  if (allSold) {
    if (label) label.textContent = 'Remova os itens esgotados';
    if (price) price.hidden = true;
    return;
  }

  if (label) label.textContent = 'Finalizar pedido';
  if (price) {
    price.hidden = false;
    if (mixed) price.textContent = formatPrice(availableTotal);
  }
}

async function removeCartItem(itemId) {
  const response = await fetch('/carrinho', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      _method: 'delete',
      item_id: itemId,
    }),
  });

  if (!response.ok) {
    throw new Error(`Falha ao remover item ${itemId}`);
  }
}

function bindCheckoutSoldOutGuard(drawerRoot) {
  if (!drawerRoot || drawerRoot.__rdCheckoutSoldGuard) return;
  drawerRoot.__rdCheckoutSoldGuard = true;

  let bypass = false;

  drawerRoot.addEventListener(
    'click',
    async (event) => {
      const cta = event.target.closest('.card-drawer-cta');
      if (!cta || !drawerRoot.contains(cta)) return;
      if (bypass) return;

      if (cta.classList.contains('rd-cart-cta--blocked') || cta.disabled) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return;
      }

      const soldItems = checkoutSoldState.soldItems || [];
      if (!soldItems.length) return;

      // Mistura: tira os esgotados e só então segue o checkout nativo.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (cta.classList.contains('rd-cart-cta--busy')) return;
      cta.classList.add('rd-cart-cta--busy');

      try {
        for (const item of soldItems) {
          if (item && item.id != null) {
            await removeCartItem(item.id);
          }
        }

        cartCache = { at: 0, data: null };
        checkoutSoldState = { soldItems: [], availableItems: checkoutSoldState.availableItems, availableTotal: checkoutSoldState.availableTotal };

        bypass = true;
        cta.click();
      } catch (error) {
        console.error('Erro ao limpar itens esgotados antes do checkout', error);
      } finally {
        window.setTimeout(() => {
          bypass = false;
          cta.classList.remove('rd-cart-cta--busy');
        }, 800);
      }
    },
    true
  );
}

function syncCartReadyState(drawerRoot) {
  const shell = drawerRoot.querySelector('.vnda-cart-drawer') || drawerRoot;
  const overlay = document.getElementById('cart-drawer-el');
  const targets = [shell, overlay].filter(Boolean);

  const setState = (loading, ready) => {
    targets.forEach((el) => {
      el.classList.toggle('rd-cart-loading', loading);
      el.classList.toggle('rd-cart-ready', ready);
    });
  };

  if (!overlay) {
    setState(false, false);
    return;
  }

  const loading = overlay.querySelector(
    '.cart-drawer-absolute.cart-drawer-inset-0.cart-drawer-bg-gray-100'
  );
  const hasItems = Boolean(overlay.querySelector('.cart-item'));
  const emptyMsg = [...overlay.querySelectorAll('p')].find((p) =>
    /seu carrinho est/i.test(p.textContent || '')
  );

  if (loading) {
    setState(true, false);
    return;
  }

  if (hasItems || emptyMsg) {
    setState(false, true);
  }
}

function observeDrawer(drawerRoot) {
  let timer = null;
  let polishing = false;

  bindCheckoutSoldOutGuard(drawerRoot);

  const run = async () => {
    if (polishing) return;
    polishing = true;

    try {
      syncCartReadyState(drawerRoot);
      polishCouponForm(drawerRoot);

      const cart = await fetchCartCached();
      const resolved = await resolveCartSoldOutItems(cart);
      await syncEmptySuggest(drawerRoot);
      filterNativeSuggested(drawerRoot, cart);
      polishCouponApplied(drawerRoot, cart);
      await polishSoldOutItems(drawerRoot, resolved);
      polishCheckoutCta(drawerRoot, resolved);
      syncCartReadyState(drawerRoot);
    } finally {
      // Libera depois do microtask pra ignorar mutações que nós mesmos geramos.
      Promise.resolve().then(() => {
        polishing = false;
      });
    }
  };

  const schedule = () => {
    if (polishing) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      run();
    }, 200);
  };

  const observer = new MutationObserver(schedule);
  observer.observe(drawerRoot, { childList: true, subtree: true });
  schedule();
}

// O CartDrawer da Vnda só anima a entrada (slideIn). No close ele dá
// rerender(false) e some na hora. Interceptamos close() pra tocar slideOut.
function patchCloseAnimation(instance) {
  if (!instance || instance.__rdClosePatched) return;

  const nativeClose = instance.close.bind(instance);
  const nativeOpen = instance.open.bind(instance);
  let closing = false;

  instance.open = function openWithoutEmptyFlash() {
    const shell =
      (CartDrawer.root && CartDrawer.root.querySelector('.vnda-cart-drawer')) ||
      CartDrawer.root;
    if (shell) {
      shell.classList.add('rd-cart-loading');
      shell.classList.remove('rd-cart-ready');
    }
    closing = false;
    // Invalida cache pra abrir com dados frescos, sem spam no observer.
    cartCache = { at: 0, data: null };
    nativeOpen();
    // Overlay monta no próximo tick — marca loading nele também.
    requestAnimationFrame(() => syncCartReadyState(CartDrawer.root));
  };

  instance.close = function closeWithAnimation() {
    if (closing) return;

    const overlay = document.getElementById('cart-drawer-el');
    const panel = overlay && overlay.querySelector('.cart-drawer-content');

    if (!overlay || !panel) {
      nativeClose();
      return;
    }

    closing = true;
    overlay.classList.add('rd-cart-closing');
    panel.classList.remove('animate__slideInRight', 'animate__slideInLeft');
    panel.classList.add('animate__animated', 'animate__faster', 'animate__slideOutRight');

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      panel.removeEventListener('animationend', finish);
      closing = false;
      nativeClose();
    };

    panel.addEventListener('animationend', finish);
    setTimeout(finish, 450);
  };

  instance.__rdClosePatched = true;
}

CartDrawer.setCartDrawer = function setCartDrawer() {
  const { settings, root } = CartDrawer;

  if (!root || !settings) return;

  const freeShipping = settings.freeShippingValue > 1 ? settings.freeShippingValue : 0;

  const componentCartDrawer = new Vnda.Component.CartDrawer({
    anchor: 'right',
    display: 'list',
    startOpen: false,
    titleCart: 'Carrinho',
    disableShippingCalculation: true,
    freeShipping,
    suggestedProductsTag: SUGGESTED_TAG,
    titleSuggested: TITLE_WITH_ITEMS,
    widthItemSuggested: 100,
    showSamplesAddedList: true,
    showSamplesQtd: true,
  });

  componentCartDrawer.render(root);
  patchCloseAnimation(componentCartDrawer);

  window.cartDrawerSettings.instance = componentCartDrawer;
  CartDrawer.settings = window.cartDrawerSettings;

  observeDrawer(root);

  root.dispatchEvent(new Event('vnda:cart-drawer-loaded'));
};

export default CartDrawer;
